import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { Code } from 'src/app/core/model/dictionary';
import { Extension } from 'src/app/extension/model/extension';
import { MessengerService } from './messenger.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { DictionaryService } from './dictionary.service';

@Injectable({
  providedIn: 'root'
})
export class ExtensionService implements OnDestroy
{
  /** Database max length for extension value storage. */
  public static readonly EXTENSION_VALUE_DB_MAX_LENGTH = 10485760;

  /**
   * List of Extension categories
   */
  public extensionCategories: Array<{ text: string; key: string; value: string }> = [
    { text: 'Care Team', key: 'CARE-TEAM', value: 'CARE_TEAM' },
    { text: 'Case', key: 'CASE', value: 'CASE' },
    { text: 'Patient', key: 'PATIENT', value: 'PATIENT' },
    { text: 'Community Service', key: 'COMMUNITY-SERVICE', value: 'COMMUNITY_SERVICE' },
    { text: 'Organization', key: 'ORGANIZATION', value: 'ORGANIZATION' },
  ];

  public extensionDataTypes: Array<{ text: string; value: string }> = [
    { text: 'Text', value: 'TEXT' },
    { text: 'Number', value: 'NUMBER' },
    { text: 'Switch', value: 'SWITCH' },
    { text: 'Date', value: 'DATE' },
    { text: 'Date/Time', value: 'DATETIME' },
    { text: 'Dictionary', value: 'DICTIONARY' },
    { text: 'Checkbox', value: 'CHECKBOX' },
    { text: 'Text (Multiline)', value: 'TEXT_AREA' },
  ];

  public textYES = '';
  public textNO = '';

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private messengerService: MessengerService,
    private dictionaryService: DictionaryService,
    private intl: IntlService,
    private translate: TranslateService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get Extension by id.
   *
   * @param id Extension id
   */
  public getExtension(id: number): Observable<any>
  {
    return this.http.get<any>(`/setting/extension/${id}`);
  }

  /**
   * Save a Extension.
   *
   * @param data Extension to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveExtension(data: Extension): Observable<any>
  {
    return this.http.post('/setting/extension', data);
  }

  /**
   * Delete Extension by id.
   *
   * @param id Extension id
   */
  public deleteExtension(id: number): Observable<any>
  {
    return this.http.delete<any>(`/setting/extension/${id}`);
  }

  /**
   * Get Extension in use.
   *
   * @param id Extension id
   */
  public isExtensionInUse(id: number): Observable<boolean>
  {
    return this.http.get<any>(`/setting/extension/inUse/${id}`);
  }

  public isExtensionByCategoryInUse(id: number, category: string): Observable<boolean>
  {
    return this.http.get<any>(`/setting/extension/inUse/${id}/${category}`);
  }

  /**
   * Get distinct Organizations by Extension Category id.
   *
   * @param category Extension Category
   */
  public getDistinctExtensionOrganizations(category: string): Observable<any>
  {
    return this.http.get<any>(`/setting/extension/organizations?category=${category}`);
  }

  /**
   * Get all setting extensions for a specific extension category.
   *
   * @param category Extension Category
   */
  public getExtensionsByCategory(category: string, allOrganizations = false, billableItem = false): Observable<any>
  {
    const organizationQueryString = allOrganizations ? `&allOrganizationsOnly=true` : '';
    const billableItemQueryString = billableItem ? `&billableItemOnly=true` : '';
    return this.http.get<any>(`/setting/extension/categories?category=${category}${organizationQueryString}${billableItemQueryString}`);
  }

  public getAllOrganizationExtensions(): Observable<any[]>
  {
    const patientExtensions = this.getExtensionsByCategory('PATIENT', true);
    const caseExtensions = this.getExtensionsByCategory('CASE', true);
    return forkJoin([patientExtensions, caseExtensions]);
  }

  public getExtensionDataType(settingExtension: Extension | any): string
  {
    const raw = settingExtension?.dataType;
    if (raw == null || raw === '')
    {
      return 'TEXT';
    }
    if (typeof raw === 'number')
    {
      const types = ['TEXT', 'NUMBER', 'SWITCH', 'DATE', 'DATETIME', 'DICTIONARY', 'CHECKBOX', 'TEXT_AREA'];
      return types[raw] ?? 'TEXT';
    }
    return this.normalizeExtensionDataType(String(raw));
  }

  public normalizeExtensionDataType(dataType: string): string
  {
    const normalized = String(dataType).toUpperCase();
    switch (normalized)
    {
      case 'TEXT':
      case 'NUMBER':
      case 'SWITCH':
      case 'DATE':
      case 'DATETIME':
      case 'DICTIONARY':
      case 'CHECKBOX':
      case 'TEXT_AREA':
        return normalized;
      default:
        return 'TEXT';
    }
  }

  public isExtensionStringDataType(dataType: string): boolean
  {
    const normalized = this.normalizeExtensionDataType(dataType);
    return normalized === 'TEXT' || normalized === 'TEXT_AREA';
  }

  public isExtensionSwitchOrCheckboxDataType(dataType: string): boolean
  {
    const normalized = this.normalizeExtensionDataType(dataType);
    return normalized === 'SWITCH' || normalized === 'CHECKBOX';
  }

  public hasExtensionDisplayValue(dataItem: any): boolean
  {
    const value = dataItem?.value;
    return value !== null && value !== undefined && value !== '';
  }

  public hasExtensionRowsPendingEdit(extensions: any[]): boolean
  {
    return extensions?.some(extension => extension?.editing) ?? false;
  }

  /**
   * Returns extensions ready for API save, or null when a row is still being edited.
   */
  public prepareExtensionsForSave(extensions: any[]): any[] | null
  {
    if (this.hasExtensionRowsPendingEdit(extensions))
    {
      return null;
    }

    return (extensions ?? []).map((extension) =>
    {
      const prepared = { ...extension };
      delete prepared.extensionEditState;
      delete prepared.editing;
      return prepared;
    });
  }

  public hasExtensionStringMaxLength(settingExtension: Extension | any): boolean
  {
    if (!this.isExtensionStringDataType(this.getExtensionDataType(settingExtension)))
    {
      return false;
    }

    const configured = Number(settingExtension?.maxLength);
    return Number.isFinite(configured) && configured > 0;
  }

  public getExtensionStringMaxLength(settingExtension: Extension | any): number
  {
    if (!this.isExtensionStringDataType(this.getExtensionDataType(settingExtension)))
    {
      return ExtensionService.EXTENSION_VALUE_DB_MAX_LENGTH;
    }

    if (this.hasExtensionStringMaxLength(settingExtension))
    {
      return Math.min(Math.trunc(Number(settingExtension.maxLength)), ExtensionService.EXTENSION_VALUE_DB_MAX_LENGTH);
    }
    return ExtensionService.EXTENSION_VALUE_DB_MAX_LENGTH;
  }

  public getExtensionNumberDecimals(settingExtension: Extension | any): number
  {
    const decimals = Number(settingExtension?.decimals);
    return Number.isFinite(decimals) && decimals >= 0 ? Math.trunc(decimals) : 0;
  }

  public getExtensionNumberFormat(settingExtension: Extension | any): string
  {
    const decimals = this.getExtensionNumberDecimals(settingExtension);
    return decimals > 0 ? `n${decimals}` : 'n0';
  }

  public parseExtensionValueForEdit(settingExtension: Extension | any, rawValue: string | boolean | number | Date | null | undefined): any
  {
    if (rawValue === null || rawValue === undefined || rawValue === '')
    {
      return null;
    }

    switch (this.getExtensionDataType(settingExtension))
    {
      case 'SWITCH':
      case 'CHECKBOX':
        return rawValue === true || rawValue === 'true';
      case 'NUMBER':
        {
          const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
          return Number.isFinite(numericValue) ? numericValue : null;
        }
      case 'DATE':
        if (rawValue instanceof Date)
        {
          return rawValue;
        }
        {
          const dateString = String(rawValue);
          return new Date(dateString.includes('T') ? dateString : `${dateString}T12:00:00.000Z`);
        }
      case 'DATETIME':
        return rawValue instanceof Date ? rawValue : new Date(String(rawValue));
      case 'DICTIONARY':
        {
          const dictionaryId = typeof rawValue === 'number' ? rawValue : Number(rawValue);
          return Number.isFinite(dictionaryId) ? dictionaryId : rawValue;
        }
      default:
        return rawValue;
    }
  }

  public serializeExtensionValueForSave(settingExtension: Extension | any, value: any): string | null
  {
    if (value === null || value === undefined || value === '')
    {
      return null;
    }

    switch (this.getExtensionDataType(settingExtension))
    {
      case 'SWITCH':
      case 'CHECKBOX':
        return value === true || value === 'true' ? 'true' : 'false';
      case 'NUMBER':
        return String(value);
      case 'DATE':
        if (value instanceof Date)
        {
          return this.intl.formatDate(value, 'yyyy-MM-dd');
        }
        return String(value);
      case 'DATETIME':
        if (value instanceof Date)
        {
          return value.toISOString();
        }
        return String(value);
      case 'DICTIONARY':
        return String(value);
      default:
        return String(value);
    }
  }

  public resolveExtensionDictionaryDisplayName(
    settingExtension: Extension | any,
    value: string | number | null | undefined,
    codes: Code[]): string | null
  {
    if (value === null || value === undefined || value === '' || !codes?.length)
    {
      return null;
    }

    const dictionaryId = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(dictionaryId))
    {
      const matchById = codes.find(code => code.id == dictionaryId);
      if (matchById)
      {
        return matchById.displayName;
      }
    }

    const valueCode = String(value);
    const matchByCode = codes.find(code => code.code === valueCode);
    return matchByCode?.displayName ?? null;
  }

  public resolveExtensionDictionaryCode(
    value: string | number | null | undefined,
    codes: Code[]): string | null
  {
    if (value === null || value === undefined || value === '')
    {
      return null;
    }

    if (!codes?.length)
    {
      return String(value);
    }

    const dictionaryId = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(dictionaryId))
    {
      const matchById = codes.find(code => code.id == dictionaryId);
      if (matchById)
      {
        return matchById.code;
      }
    }

    const valueCode = String(value);
    const matchByCode = codes.find(code => code.code === valueCode);
    return matchByCode?.code ?? valueCode;
  }

  public formatExtensionValueForExport(extension: any, codesByCodeSetId: Map<number, Code[]>): string
  {
    const value = extension?.value;
    if (value === null || value === undefined || value === '')
    {
      return '';
    }

    const settingExtension = extension?.settingExtension;
    if (this.getExtensionDataType(settingExtension) !== 'DICTIONARY')
    {
      return String(value);
    }

    const codeSetId = settingExtension?.codeSetId ?? extension?.codeSetId;
    const codes = codeSetId != null ? codesByCodeSetId.get(codeSetId) : undefined;
    return this.resolveExtensionDictionaryCode(value, codes ?? []) ?? String(value);
  }

  public formatExtensionValue(dataItem: any): any
  {
    let value = dataItem.value;
    if (value === null || value === undefined || value === '')
    {
      return value;
    }

    const dataType = this.getExtensionDataType(dataItem?.settingExtension);
    if (dataType === 'DICTIONARY')
    {
      return dataItem?.valueDisplayName ?? dataItem?.displayName ?? value;
    }

    if (dataType === 'TEXT_AREA')
    {
      return String(value).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    }

    if (dataType === 'NUMBER')
    {
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(numericValue))
      {
        return this.intl.formatNumber(numericValue, this.getExtensionNumberFormat(dataItem?.settingExtension));
      }
    }

    const isBooleanValue = this.isExtensionSwitchOrCheckboxDataType(dataType) ||
      dataItem?.fieldType === 'checkbox' || dataItem?.fieldType === 'multicheckbox' ||
      value === 'true' || value === 'false';
    const isDateValue = dataType === 'DATE' ||
      dataItem?.fieldType === 'date' || (typeof value === 'string' && value.length === 10 && value[4] === '-' && value[7] === '-');
    const isDateTimeValue = dataType === 'DATETIME' ||
      dataItem?.fieldType === 'datetime' ||
      (typeof value === 'string' && value.length > 11 && value[4] === '-' && value[7] === '-' && value[10] === 'T' && value.includes('Z'));

    if (isBooleanValue)
    {
      value = String(value).replace(/true/g, this.textYES);
      value = String(value).replace(/false/g, this.textNO);
    }

    if (isDateValue)
    {
      value = this.intl.formatDate(value instanceof Date ? value : this.intl.parseDate(value), 'd');
    }

    if (isDateTimeValue)
    {
      if (typeof value === 'string' && value.includes('Z'))
      {
        value = this.intl.formatDate(new Date(value), 'g');
      }
      else
      {
        value = this.intl.formatDate(value instanceof Date ? value : this.intl.parseDate(value), 'g');
      }
    }
    return value;
  }

  public refreshExtensionDisplayNames(value: string, extensionFormGroup: any, viewExtensions: any): void
  {
    if (extensionFormGroup && extensionFormGroup.get('codeSetId').value)
    {
      const codes = value.split(',');
      let displayNames = '';
      let alternateValues = '';
      codes.forEach((code) =>
      {
        this.dictionaryService.getCachedCodesByCodeSetId(extensionFormGroup.get('codeSetId').value)
          .subscribe({
            next: (data) =>
            {
              const lookup = data.find(x => x.code === code);
              if (lookup)
              {
                displayNames += (displayNames ? ' | ' : '') + lookup.displayName;
                if (lookup.alternateCode)
                {
                  alternateValues += (alternateValues ? ' | ' : '') + lookup.alternateCode;
                }
              }
              // Update the current editing row
              const currentRecord = viewExtensions.data.find(item => item.id === extensionFormGroup.get('id').value);
              if (currentRecord)
              {
                currentRecord.displayName = displayNames;
                currentRecord.alternateValue = alternateValues;
              }
            }
          });
      })
    }
  }

  private loadLocalizations(): void
  {
    this.translate.get(['CARE-TEAM', 'CASE', 'PATIENT', 'YES', 'NO', 'COMMUNITY-SERVICE', 'ORGANIZATION', 'EXTENSION-DATA-TYPES-LIST']).subscribe({
      next: (res) =>
      {
        this.textYES = res.YES;
        this.textNO = res.NO;
        this.extensionCategories[0].text = res['CARE-TEAM'];
        this.extensionCategories[1].text = res.CASE;
        this.extensionCategories[2].text = res.PATIENT;
        this.extensionCategories[3].text = res['COMMUNITY-SERVICE'];
        this.extensionCategories[4].text = res.ORGANIZATION;
        const dataTypeLabels = res['EXTENSION-DATA-TYPES-LIST'].split(',');
        for (let i = 0; i < dataTypeLabels.length && i < this.extensionDataTypes.length; i++)
        {
          this.extensionDataTypes[i].text = dataTypeLabels[i].trim();
        }
        this.sortExtensionDataTypesByText();
      }
    });
  }

  private sortExtensionDataTypesByText(): void
  {
    const locale = this.translate.getCurrentLang() || undefined;
    this.extensionDataTypes.sort((a, b) => a.text.localeCompare(b.text, locale, { sensitivity: 'base' }));
  }
}
